import React, { useEffect, useMemo, useRef } from 'react';
import {
  buildInitialChartEventSetting,
  hasBubbleEventContent,
} from './utils/buildInitialChartEventSetting';
import { loadOpinionChartEventSetting } from './utils/opinionChartEventSettingStorage';
import type {
  IOpinionChartEventData,
  IOpinionChartEventSetting,
} from './types';

interface IEventSettingBridgeProps {
  eventData: IOpinionChartEventData;
  xAxisData?: string[];
  dateType?: string;
  chartEventSetting: IOpinionChartEventSetting;
  onConfirm: (setting: IOpinionChartEventSetting) => void;
  settingStorageKey?: string;
}

function buildEventScopeKey(
  eventData: IOpinionChartEventData,
  xAxisData: string[],
): string {
  const listKeys = Object.keys(eventData?.eventList || {}).sort().join(',');
  const renderKeys = Object.keys(eventData?.chartRenderEventList || {})
    .sort()
    .join(',');

  return `${listKeys}::${renderKeys}::${eventData?.format || ''}::${xAxisData.join('|')}`;
}

/**
 * 情报侧事件气泡初始化：按舆情 defaultShowEvent 对应类型生成 bubbleEvent。
 * 同一 event scope 内仅初始化一次，不覆盖用户在事件设置中的勾选结果。
 */
const EventSettingBridge: React.FC<IEventSettingBridgeProps> = ({
  eventData,
  xAxisData = [],
  dateType,
  chartEventSetting,
  onConfirm,
  settingStorageKey,
}) => {
  const onConfirmRef = useRef(onConfirm);
  const chartEventSettingRef = useRef(chartEventSetting);
  const initializedScopeKeyRef = useRef<string | null>(null);

  onConfirmRef.current = onConfirm;
  chartEventSettingRef.current = chartEventSetting;

  const eventScopeKey = useMemo(
    () => buildEventScopeKey(eventData, xAxisData),
    [eventData, xAxisData],
  );

  useEffect(() => {
    if (
      !eventData?.eventList ||
      Object.keys(eventData.eventList).length === 0
    ) {
      return;
    }

    if (
      !eventData?.chartRenderEventList ||
      Object.keys(eventData.chartRenderEventList).length === 0
    ) {
      return;
    }

    if (hasBubbleEventContent(chartEventSettingRef.current.bubbleEvent)) {
      initializedScopeKeyRef.current = eventScopeKey;
      return;
    }

    if (initializedScopeKeyRef.current === eventScopeKey) {
      return;
    }

    initializedScopeKeyRef.current = eventScopeKey;

    const persistedSetting = loadOpinionChartEventSetting(settingStorageKey);
    const currentSetting = chartEventSettingRef.current;

    onConfirmRef.current(
      buildInitialChartEventSetting({
        eventData,
        currentSetting,
        persistedSetting,
        xAxisData,
        dateType,
      }),
    );
  }, [eventData, eventScopeKey, xAxisData, dateType, settingStorageKey]);

  return null;
};

export default EventSettingBridge;
